import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { CategoryOutlined as ActivityIcon, InfoOutlined as InfoIcon } from '@mui/icons-material';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { SearchFilter, segmentedSx } from '../components/monthly/MultiSelectFilter';
import { useFillViewportHeight } from '../components/monthly/tableStyles';
import StatusChip from '../components/projects/StatusChip';
import { GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../components/home/homeTheme';
import { API_ENDPOINTS } from '../config/api.config';
import { FORECAST_CONFIG } from '../config/local.config';

const EMPTY = [];
const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-paper)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}` };
const isForecast = (a) => String(a.name || '').toUpperCase() === FORECAST_CONFIG.activityName.toUpperCase();

/** Obbligo di un campo nel worklog: "Required" in evidenza, "Optional" in grigio. */
function Requirement({ required }) {
  return (
    <Box component="span" sx={{ px: 0.9, py: 0.2, borderRadius: 999, fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap', bgcolor: required ? GREEN_SOFT : 'transparent', color: required ? GREEN_DARK : MUTED }}>
      {required ? 'Required' : 'Optional'}
    </Box>
  );
}

/**
 * Activities V2: elenco in sola lettura delle activity con stato e campi obbligatori nei worklog.
 * Il backend per ora non permette di creare o modificare activity (come nella pagina classica).
 */
export default function ActivitiesV2() {
  const [activities, setActivities] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then((res) => {
        if (!res.ok) throw new Error('Error fetching activities');
        return res.json();
      })
      // L'ordine dell'API è quello usato nelle tendine di Track hours
      .then((data) => !cancelled && setActivities((data.activities || data || []).map((a, i) => ({ ...a, position: i + 1 }))))
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const isActive = (a) => a.active !== false;
  const counts = useMemo(() => ({ all: activities.length, active: activities.filter(isActive).length, inactive: activities.filter((a) => !isActive(a)).length }), [activities]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ key: 'position', dir: 'asc' });
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = activities.filter((a) => {
      if (status === 'active' && !isActive(a)) return false;
      if (status === 'inactive' && isActive(a)) return false;
      return !q || [a.name, a.description].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const sorters = {
      position: (a, b) => a.position - b.position,
      name: (a, b) => String(a.name).localeCompare(String(b.name)),
      ticket: (a, b) => Number(!!b.requires_ticket_id) - Number(!!a.requires_ticket_id),
      description: (a, b) => Number(!!b.requires_description) - Number(!!a.requires_description),
      status: (a, b) => Number(isActive(b)) - Number(isActive(a)),
    };
    list.sort((a, b) => sorters[sort.key](a, b) || sorters.position(a, b));
    if (sort.dir === 'desc') list.reverse();
    return list;
  }, [activities, search, status, sort]);

  const tableRef = useRef(null);
  const fillHeight = useFillViewportHeight(tableRef, [loading, error]);
  const sortHeader = (key, label, align) => (
    <TableCell sx={{ ...headSx, textAlign: align }} sortDirection={sort.key === key ? sort.dir : false}>
      <TableSortLabel active={sort.key === key} direction={sort.key === key ? sort.dir : 'asc'} onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === 'asc' ? 'desc' : 'asc' }))}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <PageContainer>
      <PageHeader title="Activities" subtitle={loading ? 'Loading…' : `${counts.all} activities · ${counts.active} active`} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <SearchFilter value={search} onChange={setSearch} placeholder="Search name or description" width={280} />
          <ToggleButtonGroup exclusive size="small" value={status} onChange={(e, v) => v && setStatus(v)} sx={segmentedSx}>
            <ToggleButton value="all">All ({counts.all})</ToggleButton>
            <ToggleButton value="active">Active ({counts.active})</ToggleButton>
            <ToggleButton value="inactive">Inactive ({counts.inactive})</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: MUTED }}>
            <InfoIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: 12.5, color: 'inherit' }}>Read only: activities can’t be added or changed from here yet. Each project chooses its activities in its Activities tab.</Typography>
          </Box>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box ref={tableRef} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, bgcolor: 'var(--rt-paper)', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress color="success" />
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: fillHeight ? fillHeight - 48 : 'calc(100vh - 260px)' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', minWidth: 760 }}>
                  <colgroup>
                    <col style={{ width: 64 }} />
                    <col />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 130 }} />
                    <col style={{ width: 120 }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      {sortHeader('position', '#')}
                      {sortHeader('name', 'Activity')}
                      {sortHeader('ticket', 'Ticket ID')}
                      {sortHeader('description', 'Description')}
                      {sortHeader('status', 'Status')}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((a) => {
                      const forecast = isForecast(a);
                      return (
                        <TableRow key={a.id} hover>
                          <TableCell sx={{ ...cellSx, color: MUTED, fontVariantNumeric: 'tabular-nums' }}>{a.position}</TableCell>
                          <TableCell sx={cellSx}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                              <Box sx={{ width: 30, height: 30, borderRadius: 2, bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <ActivityIcon sx={{ fontSize: 17 }} />
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                  <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: isActive(a) ? INK : MUTED }}>
                                    {a.name}
                                  </Typography>
                                  {forecast && (
                                    <Tooltip title="System activity used only for forecast planning: it isn’t actual time and projects can’t select it.">
                                      <Box component="span" sx={{ px: 0.75, py: 0.1, borderRadius: 999, fontSize: 11, fontWeight: 700, bgcolor: 'var(--rt-forecast-soft)', color: 'var(--rt-forecast-ink)', cursor: 'help' }}>
                                        System
                                      </Box>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Typography noWrap sx={{ fontSize: 12, color: MUTED }} title={a.description || ''}>
                                  {a.description || '—'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Requirement required={!!a.requires_ticket_id} />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Requirement required={!!a.requires_description} />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <StatusChip active={isActive(a)} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {!rows.length && (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ ...cellSx, py: 4, textAlign: 'center', color: MUTED }}>
                          No activities match these filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ px: 2, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
                <Typography sx={{ fontSize: 13, color: MUTED }}>
                  {rows.length} of {activities.length} activities · “Required” fields must be filled in when tracking hours on that activity
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </PageContainer>
  );
}
